import React, { useMemo, useState, useRef } from "react";
import { Table } from "antd";
// import * as XLSX from 'xlsx';
import { exportRowsToExcel } from '../../utils/excelExport';
import download_logo from '../../assets/download_logo.png'
import DropdownWithIcons from "../Modal/DropdownWithIcons";
import { useToast } from '../../components/Toast/ToastContext';
import Vector from '../../assets/Vector.png'
const ResilincFormattedTable = ({ subTierFailurePointDetails, getRiskColor, pagination }) => {
    const [sortedInfo, setSortedInfo] = useState({
        columnKey: "subTierSupplierRisk",
        order: "descend",
    });
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10; // Set page size

    // Compute and memoize table data
    const tableData = useMemo(() => {
        const data = [];
        let keyCounter = 1;
        subTierFailurePointDetails?.forEach((item, index) => {
            item.impactedTier1Suppliers.forEach((supplier, subIndex) => {
                data.push({
                    key: keyCounter++,
                    subTierSupplierRisk: item.subTierSupplierRisk? item.subTierSupplierRisk.toString():"-",
                    subTierSupplierName: item.subTierSupplierName,
                    impactedTier1Suppliers: supplier,
                    subIndex: `${index + 1}-${subIndex + 1}`,
                });
            });
        });
        return data;
    }, [subTierFailurePointDetails]);

    // Sorting logic
    // OLD LOGIC
    // const sortedData = useMemo(() => {
    //     let sorted = [...tableData];
    //     if (sortedInfo.columnKey) {
    //         sorted = sorted.sort((a, b) => {
    //             const valA = a[sortedInfo.columnKey] || "";
    //             const valB = b[sortedInfo.columnKey] || "";
    //             return sortedInfo.order === "ascend"
    //                 ? valA.localeCompare(valB)
    //                 : valB.localeCompare(valA);
    //         });
    //     }
    //     return sorted;
    // }, [tableData, sortedInfo]);

    const sortedData = useMemo(() => {
        let sorted = [...tableData];
        if (sortedInfo.columnKey) {
            sorted = sorted.sort((a, b) => {
                const valA = a[sortedInfo.columnKey];
                const valB = b[sortedInfo.columnKey];
    
                // Handle risk column specifically
                if (sortedInfo.columnKey === "subTierSupplierRisk") {
                    const isANull = valA === "-" || valA === null;
                    const isBNull = valB === "-" || valB === null;
    
                    if (isANull && isBNull) return 0;
                    if (isANull) return 1; // Always push "-" to bottom
                    if (isBNull) return -1;
    
                    const numA = parseFloat(valA);
                    const numB = parseFloat(valB);
    
                    return sortedInfo.order === "ascend" ? numA - numB : numB - numA;
                }
    
                // Fallback for string sort
                const strA = valA || "";
                const strB = valB || "";
                return sortedInfo.order === "ascend"
                    ? strA.localeCompare(strB)
                    : strB.localeCompare(strA);
            });
        }
        return sorted;
    }, [tableData, sortedInfo]);

    const paginatedData = useMemo(() => {
        const startIdx = (currentPage - 1) * pageSize;
        return sortedData.slice(startIdx, startIdx + pageSize);
    }, [sortedData, currentPage]);

    // Compute rowSpan dynamically based on sorted data
    // const rowSpanMap = useMemo(() => {
    //     const map = {};
    //     sortedData.forEach((item, index) => {
    //         if (index === 0 || item.subTierSupplierName !== sortedData[index - 1]?.subTierSupplierName) {
    //             map[item.subTierSupplierName] = sortedData.filter(i => i.subTierSupplierName === item.subTierSupplierName).length;
    //         }
    //     });
    //     return map;
    // }, [sortedData]);
    const rowSpanMap = useMemo(() => {
        const map = {};
        paginatedData.forEach((item, index) => {
            if (index === 0 || item.subTierSupplierName !== paginatedData[index - 1]?.subTierSupplierName) {
                map[item.subTierSupplierName] = paginatedData.filter(i => i.subTierSupplierName === item.subTierSupplierName).length;
            }
        });
        return map;
    }, [paginatedData]);

    // Columns with proper rowSpan handling
    const columns = [
        {
            title: "Sub-tier Supplier Risk",
            dataIndex: "subTierSupplierRisk",
            key: "subTierSupplierRisk",
            sorter: true, // Enables Ant Design sorting
            sortOrder: sortedInfo.columnKey === "subTierSupplierRisk" ? sortedInfo.order : null,
            render: (text) => <div style={{ color: getRiskColor(text), fontWeight: 700 }}>{text}</div>,
            onCell: (record, rowIndex) => ({
                // rowSpan: rowIndex > 0 && record.subTierSupplierName === sortedData[rowIndex - 1]?.subTierSupplierName
                rowSpan: rowIndex > 0 && record.subTierSupplierName === paginatedData[rowIndex - 1]?.subTierSupplierName
                    ? 0 // Hide duplicate rows after sorting
                    : rowSpanMap[record.subTierSupplierName] || 1,
            }),
        },
        {
            title: "Sub-tier Supplier Name",
            dataIndex: "subTierSupplierName",
            key: "subTierSupplierName",
            sorter: true,
            sortOrder: sortedInfo.columnKey === "subTierSupplierName" ? sortedInfo.order : null,
            onCell: (record, rowIndex) => ({
                // rowSpan: rowIndex > 0 && record.subTierSupplierName === sortedData[rowIndex - 1]?.subTierSupplierName
                rowSpan: rowIndex > 0 && record.subTierSupplierName === paginatedData[rowIndex - 1]?.subTierSupplierName
                    ? 0
                    : rowSpanMap[record.subTierSupplierName] || 1,
            }),
        },
        {
            title: "Impacted Tier 1 Suppliers",
            dataIndex: "impactedTier1Suppliers",
            key: "impactedTier1Suppliers",
            // sorter: true,
            // sortOrder: sortedInfo.columnKey === "impactedTier1Suppliers" ? sortedInfo.order : null,
        },
    ];
    
    /* istanbul ignore next */
    const downloadExcel = async () => {
        // Define the columns to keep and the desired order
        const columnsToInclude = [
          "SUB-TIER SUPPLIER RISK",
          "SUB-TIER SUPPLIER NAME",
          "IMPACTED TIER 1 SUPPLIERS"
        ];
        
        const columnMapping = {
          "subTierSupplierRisk": "SUB-TIER SUPPLIER RISK",
          "subTierSupplierName": "SUB-TIER SUPPLIER NAME",
          "impactedTier1Suppliers": "IMPACTED TIER 1 SUPPLIERS"
        };
      
        // Transform the data
        const transformedData = tableData.map(item => {
          const newItemRFTable = {};
          columnsToInclude.forEach(col => {
            const originalKey = Object.keys(columnMapping).find(key => columnMapping[key] === col);
            newItemRFTable[col] = item[originalKey];
          });
          return newItemRFTable;
        });
      
        await exportRowsToExcel({
          rows: transformedData,
          headers: columnsToInclude,
          fileName: 'SUB-TIER FAILURE POINTS.xlsx',
        });
      };

      const subTierFailureRef = useRef()

      const { showToast } = useToast();

      /* istanbul ignore next */
      const checkPDFDownloaded = (isPdfDownloaded, isModal, fileURL, isExcel, isTable, isJpeg) => {
        if(isPdfDownloaded){
            if(isModal){
                showToast('success', !isJpeg ? "PDF of full page successfully downloaded." : 'JPEG of full page successfully downloaded.', {
                    icon: <img src={Vector} alt='image' style={{ width: '20px', height: '20px' }} />
                });
            } else if(isTable){
                showToast('success', !isJpeg ? "PDF of a table successfully downloaded." : 'JPEG of a table successfully downloaded.', {
                    icon: <img src={Vector} alt='image' style={{ width: '20px', height: '20px' }} />
                });
            }
            else{
                showToast('success', !isJpeg ? "PDF of a chart successfully downloaded.": 'JPEG of a chart successfully downloaded.', {
                    icon: <img src={Vector} alt='image' style={{ width: '20px', height: '20px' }} />
                });
            }
        }
        else if(isExcel){
            downloadExcel()
        }
        else{
            showToast('Error', "Failed to download. Please try again.", {
                icon: <img src={Vector} alt='image' style={{ width: '20px', height: '20px' }} />
            });
        }   
      }


    return (
        <>
        <div ref={subTierFailureRef}>
            <div className="archlet-chart-title" >
                Sub-tier Failure Points                             
             </div>   
            
            {/* <button type="primary" title='Download Data' onClick={downloadExcel} className="download-excel-resilinc">
                <img src={download_logo} alt="download_logo" style={{position: 'relative', top: '2px'}}/>
            </button> */}
            <span style={{float: 'right', position: 'relative', bottom: '1.5rem'}}>
                <DropdownWithIcons modalRef={subTierFailureRef} kpiName={"Sub-tier Failure Points"} checkPDFDownloaded={checkPDFDownloaded} isTable={true}/>
            </span>
            <Table
                className="commonTable"
                columns={columns}
                dataSource={sortedData}
                bordered
            //  pagination={true}
            // onChange={(pagination, filters, sorter) => setSortedInfo(sorter)}
            pagination={{
                position: ["bottomCenter"],
                pageSize,
                current: currentPage,
                total: sortedData.length,
                onChange: (page) => setCurrentPage(page),
                showSizeChanger: false,
            }}
            onChange={(pagination, filters, sorter) => setSortedInfo(sorter)}
            />
        </div>
        </>
    );
};

export default ResilincFormattedTable;
